import { DataTable, Notice } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { tPlural } from "@/lib/i18n/interpolate";
import type {
  AgeStats,
  PyramidData,
} from "@/lib/tool-kit/earth-science/population-pyramid";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

/** Every label the summary needs; the widget passes its own strings. */
export interface PyramidSummaryLabels {
  summaryHeading: string;
  statMetric: string;
  statUnder15: string;
  statWorking: string;
  statOver65: string;
  statDependency: string;
  statYouth: string;
  statOldAge: string;
  statSexRatio: string;
  statMedian: string;
  statTotal: string;
  notAvailable: string;
  skippedLines: string;
  missingCells: string;
  negativeCells: string;
  reordered: string;
}

const ONE = { maximumFractionDigits: 1 };

function percent(fmt: Fmt, value: number | null, none: string): string {
  return value === null ? none : `${fmt(value, ONE)} %`;
}

function plain(fmt: Fmt, value: number | null, none: string): string {
  return value === null ? none : fmt(value, ONE);
}

/** Age-band figures for one or two populations, as a table with a column each. */
export function PyramidSummary({
  labels,
  fmt,
  columns,
  showTotal,
}: {
  labels: PyramidSummaryLabels;
  fmt: Fmt;
  columns: { name: string; stats: AgeStats | null; total: number }[];
  showTotal: boolean;
}) {
  const none = labels.notAvailable;
  const rows: [string, (column: (typeof columns)[number]) => string][] = [
    [labels.statUnder15, (c) => percent(fmt, c.stats?.under15 ?? null, none)],
    [labels.statWorking, (c) => percent(fmt, c.stats?.working ?? null, none)],
    [labels.statOver65, (c) => percent(fmt, c.stats?.over65 ?? null, none)],
    [
      labels.statDependency,
      (c) => plain(fmt, c.stats?.dependencyRatio ?? null, none),
    ],
    [labels.statYouth, (c) => plain(fmt, c.stats?.youthRatio ?? null, none)],
    [labels.statOldAge, (c) => plain(fmt, c.stats?.oldAgeRatio ?? null, none)],
    [labels.statSexRatio, (c) => plain(fmt, c.stats?.sexRatio ?? null, none)],
    [labels.statMedian, (c) => plain(fmt, c.stats?.medianAge ?? null, none)],
  ];
  if (showTotal) {
    rows.unshift([labels.statTotal, (c) => fmt(c.total)]);
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{labels.summaryHeading}</h3>
      <DataTable
        head={[labels.statMetric, ...columns.map((column) => column.name)]}
        rows={rows.map(([name, read]) => [name, ...columns.map(read)])}
      />
    </>
  );
}

/** What the reader should know about how their data was read. */
export function PyramidNotices({
  data,
  labels,
  locale,
  fmt,
}: {
  data: PyramidData;
  labels: PyramidSummaryLabels;
  locale: string;
  fmt: Fmt;
}) {
  const notes: string[] = [];
  if (data.skipped.length > 0) {
    notes.push(
      tPlural(labels.skippedLines, locale, data.skipped.length, {
        lines: data.skipped.map((line) => fmt(line)).join(", "),
      })
    );
  }
  if (data.missingCells > 0) {
    notes.push(
      tPlural(labels.missingCells, locale, data.missingCells, {
        count: fmt(data.missingCells),
      })
    );
  }
  if (data.negativeCells > 0) {
    notes.push(
      tPlural(labels.negativeCells, locale, data.negativeCells, {
        count: fmt(data.negativeCells),
      })
    );
  }
  if (data.reordered) {
    notes.push(labels.reordered);
  }
  if (notes.length === 0) {
    return null;
  }
  return (
    <div aria-live="polite" className="space-y-2">
      {notes.map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
    </div>
  );
}
